import {
  Building2Icon,
  FactoryIcon,
  FileSearchIcon,
  Globe2Icon,
  GraduationCapIcon,
  LeafIcon,
  RecycleIcon,
  SproutIcon,
  ThermometerSunIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Environmental Management Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to environmental management can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is environmental management and why it matters',
      'Human activities and environmental change',
      'Sustainability as a framework',
      'Prevention, mitigation, and restoration',
      'Environmental decision-making',
      'Stakeholder roles and responsibility',
      'Environmental governance',
      'The transition from problems to solutions',
      'Professional terminology',
      'The complete environmental management approach',
    ],
    result:
      'Your students understand environmental management as a practical discipline that connects science, policy, and human action — not an impossible list of terms to memorize.',
    icon: Globe2Icon,
  },
  {
    number: '2',
    title: 'Ecosystems, Biodiversity & Natural Resources',
    subtitle: 'Everything your students need to know about natural systems and their management',
    context:
      'This is where many students struggle to connect ecological principles to management practice. This volume gives you the materials to help them truly understand ecosystem importance.',
    items: [
      'Ecosystem structure and function',
      'Ecological interactions and relationships',
      'Biodiversity: types, distribution, and importance',
      'Threats to natural systems and species',
      'Conservation strategies',
      'Natural resources: types and sustainability',
      'Water resources and management',
      'Soil resources and degradation',
      'Forest ecosystems and management',
      'Ecosystem services and their value',
      'Human pressures on ecosystems',
      'Sustainable approaches to resource management',
      '12 practical ecosystem management case studies',
    ],
    result: 'Your students learn how ecosystems function and why their sustainable management is critical.',
    icon: LeafIcon,
  },
  {
    number: '3',
    title: 'Pollution Prevention & Environmental Quality',
    subtitle: 'How to understand, prevent, and manage pollution',
    context:
      'Pollution is complex but essential to understand. This volume gives you everything needed so your students grasp pollution sources, pathways, and solutions.',
    items: [
      'Air pollution: sources, impacts, and control',
      'Water pollution: types, causes, and remediation',
      'Soil pollution and contamination',
      'Waste-related pollution and environmental effects',
      'Noise and other environmental pressures',
      'Pollution pathways and human exposure',
      'Environmental quality standards and monitoring',
      'Prevention strategies and best practices',
      'Pollution control technologies',
      'Environmental remediation approaches',
      'Regulatory frameworks',
      '15 worked pollution management examples',
    ],
    result:
      'Your students understand pollution as a solvable problem and learn practical prevention and management strategies.',
    icon: FactoryIcon,
  },
  {
    number: '4',
    title: 'Environmental Impact Assessment & Risk Management',
    subtitle: 'Systematic approaches to evaluating environmental consequences',
    context: 'Environmental Impact Assessment is complex but essential. This volume makes the process clear and applicable.',
    items: [
      'Environmental Impact Assessment fundamentals',
      'Screening and scoping procedures',
      'Baseline environmental conditions',
      'Impact identification methods',
      'Impact significance evaluation',
      'Mitigation measures and alternatives',
      'Cumulative impact assessment',
      'Environmental risk management',
      'Monitoring and follow-up procedures',
      'Adaptive management strategies',
      'Application to real-world projects',
      'Decision-making and environmental consequences',
      '10 practical EIA case studies',
    ],
    result:
      'Your students understand how to systematically evaluate environmental consequences and make informed decisions.',
    icon: FileSearchIcon,
  },
  {
    number: '5',
    title: 'Waste Management & Circular Economy',
    subtitle: 'From linear consumption to circular solutions',
    context:
      'Waste and circular economy concepts are increasingly important. This volume connects waste challenges to sustainable solutions.',
    items: [
      'Waste types and classification',
      'Environmental impacts of waste',
      'Waste prevention and reduction strategies',
      'Reuse and repair approaches',
      'Recycling processes and systems',
      'Collection, treatment, and disposal methods',
      'Hazardous waste management',
      'Circular economy principles',
      'Responsible consumption patterns',
      'Resource efficiency and waste minimization',
      'Circular economy business models',
      'Practical waste reduction strategies',
      '12 circular economy implementation case studies',
    ],
    result: 'Your students understand the shift from linear to circular systems and their role in sustainability.',
    icon: RecycleIcon,
  },
  {
    number: '6',
    title: 'Environmental Management Systems & Sustainable Organizations',
    subtitle: 'How organizations operationalize environmental responsibility',
    context:
      'Environmental Management Systems connect policy to practice. This volume shows how organizations implement sustainability.',
    items: [
      'Environmental Management Systems frameworks',
      'Environmental policies and commitments',
      'Aspects and impacts identification',
      'Objectives, targets, and planning',
      'Organizational responsibilities',
      'Operational control procedures',
      'Environmental performance monitoring',
      'Corrective and preventive action',
      'Environmental communication and transparency',
      'Continuous improvement cycles',
      'Certification and compliance',
      'Leadership and organizational change',
      '10 organizational case studies',
    ],
    result: 'Your students understand how environmental responsibility is embedded in organizational operations.',
    icon: Building2Icon,
  },
  {
    number: '7',
    title: 'Climate Change, Energy & Environmental Challenges',
    subtitle: 'Understanding the interconnected environmental challenges we face',
    context:
      'Climate change and major environmental challenges are complex but critical. This volume provides comprehensive understanding.',
    items: [
      'Climate change: causes and consequences',
      'Greenhouse gas emissions and sources',
      'Climate impacts on ecosystems and society',
      'Energy systems and environmental effects',
      'Renewable and non-renewable energy',
      'Energy efficiency and conservation',
      'Resource depletion and scarcity',
      'Environmental pressures and limits',
      'Adaptation and resilience strategies',
      'Mitigation approaches',
      'Policy responses and agreements',
      'Individual and collective action',
      '15 climate and environmental challenge case studies',
    ],
    result:
      'Your students understand interconnected environmental challenges and recognize multiple response strategies.',
    icon: ThermometerSunIcon,
  },
  {
    number: '8',
    title: 'Sustainable Development & Environmental Solutions',
    subtitle: 'Connecting environmental management to sustainable futures',
    context:
      'This is the practical heart. Your students learn how communities and organizations can respond to environmental problems.',
    items: [
      'Sustainable development concepts and goals',
      'Environmental justice and equity',
      'Community-based environmental management',
      'Green economy and sustainable business',
      'Renewable resource management',
      'Environmental restoration and rehabilitation',
      'Nature-based solutions',
      'Environmental entrepreneurship',
      'Technology and environmental innovation',
      'Policy and regulatory approaches',
      'International environmental agreements',
      'Local action and global impact',
      'Building environmental resilience',
      '15 real-world environmental solutions case studies',
    ],
    result:
      'Your students understand that environmental challenges have solutions and recognize diverse pathways to sustainability.',
    icon: SproutIcon,
  },
  {
    number: '9',
    title: 'Teaching Environmental Management: Classroom Excellence',
    subtitle: 'Your complete guide as an environmental education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of environmental management teaching.',
    items: [
      'The educational value of environmental management',
      'Establishing clear learning objectives',
      'Organizing environmental content by difficulty level',
      'Designing effective lesson sequences',
      'Environmental problem-based learning approaches',
      'Case-based learning strategies',
      'Classroom discussions and guided debates',
      'Decision-making and environmental scenarios',
      'Environmental projects and investigations',
      'Field-based learning experiences',
      'Assessment strategies for environmental learning',
      'Rubrics and performance criteria',
      'Addressing common environmental misconceptions',
      'Differentiation and inclusive instruction',
      'Interdisciplinary environmental education',
      '10 complete integrated environmental projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
